import { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { generatePath, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import getComponentName from 'react-utils/getComponentName';
import { NavMarker } from 'react-rhumb';
import memoize from 'transmute/memoize';
import { parseLocation } from 'automation-ui-routing/utils';
import { getObjectTypeId, getActionOutputsForTargetActionById } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import * as UsageTrackingScreens from 'automation-ui-usage-tracking/UsageTrackingScreens';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { ScreenTrackingContextProvider } from 'automation-ui-usage-tracking/ScreenTrackingContext';
import { trackPageView, trackEvent } from 'automation-ui-canvas/utils/trackingUtils';
import * as flowActions from 'automation-ui-canvas/actions/FlowActions';
import FlowCanvasNode from 'automation-ui-canvas/components/canvas/FlowCanvasNode';
import Action from 'automation-ui-action-features/records/Action';
import PannableDecorator from 'automation-ui-canvas/decorators/PannableDecorator';
import ZoomableDecorator from 'automation-ui-canvas/decorators/ZoomableDecorator';
import { translateToGraph } from 'automation-ui-canvas/utils/platformWorkflowTranslators';
import { attachWrappedComponent } from 'automation-ui-core/decoratorUtils';
import { getSourceApp } from 'automation-ui-canvas/selectors/embedSelectors';
import { getActionById } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import * as SpecialActionIds from 'automation-ui-flow-core/canvas/SpecialActionIds';
import { WAIT_UNTIL } from 'automation-ui-action-features/constants/ActionTypes';
import { getIsValidActionForDataSource } from 'automation-ui-action-features/data-passing/utils/getIsValidActionForDataSource';
import { getActionMetadataConfigFromAction } from 'automation-ui-action-features/actions/ActionMetadataConfigUtils';
import { mapActionTypeToRoute } from 'automation-ui-canvas/utils/data-passing/mapActionTypeToRoute';
import { wireActionOutputs } from 'automation-ui-action-features/queries/actionOutputQueries';
import { wireAuthConsumer } from 'automation-ui-core/context/AuthContext';
import { wireUpdateActionAndSaveFlow, wireFlowById } from 'automation-ui-canvas/queries/flowQueries';
import uniqueIdForObject from 'automation-ui-core/utils/uniqueIdForObject';
import { ALL_ACTIONS_LIST, CANVAS_ROOT, PLATFORM_ACTION_EDIT } from 'automation-ui-canvas/constants/AbsolutePaths';
import { getScreenForFlowType } from 'automation-ui-canvas/utils/getScreenForFlowType';
import { buildSearchStringForActionSetRoutes } from 'automation-ui-canvas/utils/buildSearchStringForActionSetRoutes';
import { getZoomLevel } from 'automation-ui-canvas/selectors/localStorageSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledCanvas = styled.div.withConfig({
  displayName: "FlowCanvasDataSourceEditDecorator__StyledCanvas",
  componentId: "sc-1pddaxl-0"
})(["height:100%;"]);
function getComposedCanvas() {
  return PannableDecorator(
  // TODO(TS_BUG)
  // @ts-expect-error ZoomableDecorator expects StyledCanvas to have its required props, but it's just a div
  // and the required props aren't wired until ComposedCanvas is used in FlowCanvasBase. Not sure if real error or not
  ZoomableDecorator({
    WrappedComponent: StyledCanvas
  }));
}
export default function FlowCanvasDataSourceEditDecorator(WrappedComponent, centerOnMount) {
  const componentName = getComponentName(WrappedComponent);
  class FlowCanvasDataSourceEdit extends Component {
    constructor(...args) {
      super(...args);
      this.state = {
        actionIdsForDataSourceAction: undefined,
        selectedDataSourceActionId: null,
        currentStateFromProps: {
          flowId: this.props.flowId,
          flow: this.props.flow
        }
      };
      this.ComposedCanvas = getComposedCanvas();
      this.keydownEventListener = event => {
        const {
          key
        } = event;
        if (key === 'Escape') {
          this.redirectToFullCanvas();
        }
      };
      this.setEscapeKeyEventListener = () => {
        document.addEventListener('keydown', this.keydownEventListener);
      };
      this.removeEscapeKeyEventListener = () => {
        document.removeEventListener('keydown', this.keydownEventListener);
      };
      this.handleGetActionOutputsForTargetActionById = (targetActionId, isNew) => getActionOutputsForTargetActionById({
        flow: this.props.flow,
        allOutputsByActionTypeId: this.props.allOutputsByActionTypeId,
        instanceOutputsByActionId: this.props.instanceOutputsByActionId,
        targetActionId,
        isNewAction: isNew
      });
      this.toggleAddEmptyActionToFlow = () => {
        const {
          flow,
          updateFlowLocally,
          edgeId,
          currentActionId
        } = this.props;
        if (!flow || currentActionId !== SpecialActionIds.NEW_ACTION) {
          return;
        }
        const emptyAction = new Action({
          actionType: WAIT_UNTIL,
          actionId: SpecialActionIds.NEW_ACTION
        });
        let newFlow = flow;
        const addAction = !!edgeId;
        if (addAction) {
          newFlow = newFlow.removeAction(SpecialActionIds.NEW_ACTION);
          newFlow = newFlow.insertAction({
            action: emptyAction,
            edgeId,
            actionId: SpecialActionIds.NEW_ACTION
          });
        } else {
          newFlow = newFlow.removeAction(SpecialActionIds.NEW_ACTION);
        }
        updateFlowLocally(newFlow);
      };
      this.onRenderCanvasNode = memoize(({
        id,
        width,
        height
      }) => {
        const {
          flow,
          showEnrollmentCard,
          selectedActionMetadataConfig,
          currentActionId,
          zoomLevel
        } = this.props;
        const {
          actionIdsForDataSourceAction
        } = this.state;
        const canSelect = actionIdsForDataSourceAction.includes(id);
        if (id === SpecialActionIds.ENROLLMENT && !showEnrollmentCard) {
          return null;
        }
        return /*#__PURE__*/_jsx(FlowCanvasNode, {
          id: id,
          width: width,
          height: height,
          flow: flow,
          readOnly: true,
          isSelected: id.toString() === currentActionId,
          isDisabled: !canSelect,
          getActionOutputsForTargetActionById: this.handleGetActionOutputsForTargetActionById,
          selectedActionMetadataConfig: selectedActionMetadataConfig,
          onSelectNode: this.onSelectAction,
          zoomLevel: zoomLevel
        });
      }, ({
        id: nodeId
      }) => `${nodeId}-${uniqueIdForObject(this.props.flow)}-${this.props.flowId}-${this.props.currentActionId}-${this.state.selectedDataSourceActionId}-${this.state.actionIdsForDataSourceAction}-${this.props.zoomLevel}`);
      this.onSelectAction = id => {
        const dataSourceActionId = id;
        const {
          flow,
          flowId,
          destinationActionType,
          edgeId,
          history,
          location,
          currentActionId
        } = this.props;
        trackEvent(Trackables.SELECT_DATA_SOURCE_ACTION);
        const configRoute = mapActionTypeToRoute({
          actionType: destinationActionType
        });
        const screen = getScreenForFlowType(flow.flowType);
        const queryParams = {
          edgeId,
          dataSourceActionId
        };
        const search = buildSearchStringForActionSetRoutes(flow, location, queryParams);
        return history.replace({
          pathname: generatePath(PLATFORM_ACTION_EDIT, {
            flowId,
            screen,
            actionId: currentActionId,
            configRoute
          }),
          search
        });
      };
      this.onClickAddControl = edgeId => {
        // This is used for when click the + to add new action when in this state
        const {
          history,
          location,
          flowId,
          edgeId: selectedEdgeId,
          flow
        } = this.props;
        const removeEdgeId = selectedEdgeId === edgeId;
        if (removeEdgeId) {
          return this.redirectToFullCanvas();
        }
        if (!removeEdgeId) {
          trackEvent(Trackables.ADD_ACTION_CONFIG, {
            objectTypeId: getObjectTypeId(flow)
          });
        }
        const queryParams = {
          edgeId
        };
        const search = buildSearchStringForActionSetRoutes(flow, location, queryParams);
        const screen = getScreenForFlowType(flow.flowType);
        const pathname = generatePath(ALL_ACTIONS_LIST, {
          flowId,
          screen
        });
        return history.replace({
          pathname,
          search
        });
      };
      this.redirectToFullCanvas = () => {
        const {
          flowId,
          history,
          location,
          flow
        } = this.props;
        const screen = getScreenForFlowType(flow.flowType);
        const pathname = generatePath(CANVAS_ROOT, {
          flowId,
          screen
        });
        const search = buildSearchStringForActionSetRoutes(flow, location);
        return history.replace({
          pathname,
          search
        });
      };
    }
    static getDerivedStateFromProps(props, state) {
      const {
        flowId,
        flow,
        previousActionId,
        destinationActionType
      } = props;
      const {
        currentStateFromProps
      } = state;
      if (flowId !== currentStateFromProps.flowId) {
        FlowCanvasDataSourceEdit.handleRequestFlow(props);
      }
      let actionIdsForDataSourceAction = state.actionIdsForDataSourceAction;
      if (flow !== currentStateFromProps.flow || !actionIdsForDataSourceAction) {
        actionIdsForDataSourceAction = FlowCanvasDataSourceEdit.setValidDataSourceActionIds(flow, previousActionId, destinationActionType);
      }
      return {
        actionIdsForDataSourceAction,
        currentStateFromProps: {
          flowId,
          flow
        }
      };
    }
    componentDidMount() {
      this.setEscapeKeyEventListener();
      trackPageView(UsageTrackingScreens.PLATFORM_FLOW_DATA_SOURCE_EDIT_CONTAINER);
      this.toggleAddEmptyActionToFlow();
    }
    componentWillUnmount() {
      this.removeEscapeKeyEventListener();
    }
    render() {
      const {
        flow,
        canvasScrollElementRef,
        currentActionId,
        zoomLevel
      } = this.props;
      if (!flow) {
        return null;
      }

      // EJM - override on action set only
      const onRenderEndMarker = flow.isActionSet() ? () => null : undefined;
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "DATA_SOURCE_EDIT_CANVAS",
        children: /*#__PURE__*/_jsx(ScreenTrackingContextProvider, {
          value: UsageTrackingScreens.PLATFORM_FLOW_DATA_SOURCE_EDIT_CONTAINER,
          children: /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props, {
            flow: flow,
            onClickAddControl: this.onClickAddControl,
            canvasScrollElementRef: canvasScrollElementRef,
            onRenderCanvasNode: this.onRenderCanvasNode,
            onSelectAction: this.onSelectAction,
            ComposedCanvas: this.ComposedCanvas,
            dataSourceEditNodeId: currentActionId,
            selectedDataSourceActionId: this.state.selectedDataSourceActionId,
            onRenderEndMarker: onRenderEndMarker,
            centerOnMount: centerOnMount,
            zoomLevel: zoomLevel
          }))
        })
      });
    }
  }
  FlowCanvasDataSourceEdit.defaultProps = {
    canvasScrollElementRef: null,
    destinationActionType: WAIT_UNTIL
  };
  FlowCanvasDataSourceEdit.getActionIdsForDataSourceAction = flowAsGraph => {
    return memoize((previousActionId, flow, destinationActionType) => {
      const parentNodesIds = flowAsGraph.getNodeIdsForDataSourceNode(previousActionId);
      const allowedNodeIds = parentNodesIds.filter(parentNodeId => {
        const action = flow.getActionById(parentNodeId);
        return action && getIsValidActionForDataSource({
          action,
          destinationActionType
        });
      });
      return allowedNodeIds;
    }, (previousActionId, flow, destinationActionType) => `${previousActionId}-${uniqueIdForObject(flow)}-${destinationActionType}`);
  };
  FlowCanvasDataSourceEdit.setValidDataSourceActionIds = (flow, previousActionId, destinationActionType = WAIT_UNTIL) => {
    if (!flow) {
      return [];
    }
    const flowAsGraph = translateToGraph(flow, true);
    const actionIdsForDataSourceAction = FlowCanvasDataSourceEdit.getActionIdsForDataSourceAction(flowAsGraph)(previousActionId, flow, destinationActionType);
    return actionIdsForDataSourceAction;
  };
  FlowCanvasDataSourceEdit.handleRequestFlow = ({
    flowQuery,
    flowId
  }) => {
    if (flowId > 0) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      flowQuery.refetch({
        flowId
      });
    }
  };
  const ConnectedFlowCanvasDataSourceEdit = compose(withRouter, wireAuthConsumer, _WrappedComponent => props => {
    const {
      flowId: stringId
    } = props.match.params;
    const flowId = Number(stringId);
    return /*#__PURE__*/_jsx(_WrappedComponent, Object.assign({}, props, {
      flowId: flowId
    }));
  }, wireFlowById, connect((state, ownProps) => {
    const {
      flow,
      flowId
    } = ownProps;
    const {
      actionId: currentActionId
    } = ownProps.match.params;
    const parsedQuery = parseLocation(ownProps.location);

    // TODO: Edit state will need to pass selected actionId - ejm
    const {
      edgeId,
      updateConnection,
      actionType
    } = parsedQuery;

    // TODO: This might not scale? may need to rethink routing - ejm
    // Second TODO ZS 12/9/20: getActionMetadataConfigFromAction takes an
    // Action record because it needs access to the
    // metadata in certain cases. This could break.
    const selectedActionMetadataConfig = actionType ? getActionMetadataConfigFromAction({
      actionType: actionType
    }, undefined, ownProps.extensionDefinitions) : null;
    const previousActionIdModifier = currentActionId === SpecialActionIds.NEW_ACTION ? edgeId : currentActionId;
    const previousActionId = previousActionIdModifier ? previousActionIdModifier.split('-edge-')[0] : null;
    const previousAction = getActionById(flow, Number(previousActionId));
    const showEnrollmentCard = !flow || !flow.isActionSet();
    return {
      destinationActionType: actionType,
      activeFlowId: Number(parsedQuery.activeFlowId),
      currentActionId,
      edgeId,
      flow,
      flowId,
      previousAction,
      previousActionId,
      selectedActionMetadataConfig,
      sourceApp: getSourceApp(state),
      updateConnection: updateConnection === 'true',
      showEnrollmentCard,
      zoomLevel: getZoomLevel(flowId)(state)
    };
  }, {
    updateFlowLocally: flowActions.updateFlowLocally
  }), wireUpdateActionAndSaveFlow, _WrappedComponent => props => wireActionOutputs(_WrappedComponent, {
    flow: props.flow
  })(props))(FlowCanvasDataSourceEdit);
  ConnectedFlowCanvasDataSourceEdit.displayName = `ConnectedFlowCanvasDataSourceEdit(${componentName})`;
  attachWrappedComponent(ConnectedFlowCanvasDataSourceEdit, WrappedComponent);
  return ConnectedFlowCanvasDataSourceEdit;
}